import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getKidsProductById, getKidsProductsByCategory } from '../data/kidsProducts';
import KidsProductCard from '../components/KidsProductCard';
import { cartService } from '../services/cartService';
import { useAuth } from '../contexts/AuthContext';
import { discountBadgeColor } from '../utils/festivePrice';

const KidsProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [isWide, setIsWide] = useState(window.innerWidth > 768);

  useEffect(() => {
    window.scrollTo(0, 0);
    setSelectedSize(null);
    setQuantity(1);
  }, [id]);

  useEffect(() => {
    const handleResize = () => {
      setIsWide(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const product = id ? getKidsProductById(id) : undefined;

  if (!product) {
    return (
      <div className="min-h-screen bg-[#FCFCFC] flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">❌</div>
          <h2 className="text-2xl font-bold mb-2">Product Not Found</h2>
          <p className="text-gray-600 mb-4">The product you're looking for doesn't exist</p>
          <button
            onClick={() => navigate('/kids')}
            className="bg-[#DAA520] text-white px-6 py-2 rounded hover:bg-[#c5941c]"
          >
            Back to Kids Collection
          </button>
        </div>
      </div>
    );
  }

  // Get related products (same category)
  const relatedProducts = getKidsProductsByCategory(product.category)
    .filter(p => p.id !== product.id)
    .slice(0, 4);

  const handleAddToCart = () => {
    if (!selectedSize) {
      alert('Please select a size');
      return;
    }
    if (!user) {
      alert('Please login to add items to cart');
      navigate('/login');
      return;
    }
    cartService.addToCart(user.id, {
      productId: product.id,
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice,
      imageUrl: product.imageUrl,
      category: product.category,
      quantity,
      selectedSize,
      selectedColor: product.availableColors[0] || undefined,
    });
    window.dispatchEvent(new Event('storage'));
    alert(`Added ${product.name} to cart`);
  };

  const handleBuyNow = () => {
    if (!selectedSize) {
      alert('Please select a size');
      return;
    }
    if (!user) {
      navigate('/login');
      return;
    }
    cartService.addToCart(user.id, {
      productId: product.id,
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice,
      imageUrl: product.imageUrl,
      category: product.category,
      quantity,
      selectedSize,
      selectedColor: product.availableColors[0] || undefined,
    });
    navigate('/checkout');
  };

  return (
    <div className="min-h-screen bg-[#FCFCFC]">
      {/* Product Section */}
      <div className={`py-8 px-4 ${isWide ? 'md:px-20' : ''}`}>
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/kids')}
              className="text-gray-600 hover:text-[#DAA520] flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Kids Collection
            </button>
          </div>

          <div className={`${isWide ? 'flex gap-8' : ''}`}>
            {/* Product Image */}
            <div className={`${isWide ? 'flex-1' : 'mb-8'}`}>
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="aspect-square flex items-center justify-center p-8">
                  <img
                    src={encodeURI(product.imageUrl)}
                    alt={product.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Product Details */}
            <div className={`${isWide ? 'flex-1' : ''}`}>
              <div className="bg-white rounded-lg shadow-lg p-6">
                {/* Category Badge */}
                <div className="inline-block mb-4">
                  <span className="bg-gray-100 text-gray-700 text-sm px-3 py-1 rounded">
                    {product.category.charAt(0).toUpperCase() + product.category.slice(1)}
                  </span>
                </div>

                {/* Product Name */}
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
                  {product.name}
                </h1>

                {/* Price - Festive Offer Display */}
                <div className="mb-6 bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-100">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`${discountBadgeColor(product.discountPercent)} text-white text-sm font-bold px-3 py-1 rounded`}>
                      {product.discountPercent}% OFF
                    </span>
                    <span className="text-xs text-red-600 font-semibold">🎉 FESTIVE OFFER</span>
                  </div>
                  <div className="flex items-baseline gap-3 mb-1">
                    <span className="text-4xl font-bold text-[#E91E63]">₹{product.price}</span>
                    <span className="text-xl text-gray-400 line-through">₹{product.originalPrice}</span>
                  </div>
                  <p className="text-sm text-green-700 font-semibold mb-2">You Save: ₹{product.amountSaved}</p>
                  <p className="text-xs text-gray-600 italic">✓ Inclusive of all taxes</p>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">Description</h3>
                  <p className="text-gray-600">{product.description}</p>
                </div>

                {/* Size Selector */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">Select Size</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.availableSizes.map(size => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`px-4 py-2 border rounded ${
                          selectedSize === size
                            ? 'bg-[#DAA520] text-white border-[#DAA520]'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-[#DAA520]'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quantity and Actions */}
                <div className="mb-6">
                  <div className="flex items-center mb-4">
                    <div className="border border-gray-300 rounded flex items-center mr-4">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="px-3 py-2 text-gray-600 hover:text-gray-800"
                        disabled={quantity <= 1}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </button>
                      <span className="px-4 py-2 text-lg font-semibold">{quantity}</span>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="px-3 py-2 text-gray-600 hover:text-gray-800"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>

                    <div className="flex-1 space-x-3">
                      <button
                        onClick={handleAddToCart}
                        className="flex-1 bg-[#E91E63] text-white py-3 rounded font-medium hover:bg-[#D81B60]"
                      >
                        Add to Cart
                      </button>
                      <button
                        onClick={handleBuyNow}
                        className="flex-1 border border-[#DAA520] text-[#DAA520] py-3 rounded font-medium hover:bg-[#DAA520]/10"
                      >
                        Buy Now
                      </button>
                    </div>
                  </div>
                </div>

                {/* Product Info */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold mb-3">Product Information</h3>
                  <div className="space-y-2">
                    <div className="flex">
                      <span className="w-32 text-gray-600">Category:</span>
                      <span className="font-medium">{product.category.charAt(0).toUpperCase() + product.category.slice(1)}</span>
                    </div>
                    <div className="flex">
                      <span className="w-32 text-gray-600">Available Sizes:</span>
                      <span className="font-medium">{product.availableSizes.join(', ')}</span>
                    </div>
                    <div className="flex">
                      <span className="w-32 text-gray-600">Available Colors:</span>
                      <span className="font-medium">{product.availableColors.join(', ')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                You May Also Like
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {relatedProducts.map(relatedProduct => (
                  <KidsProductCard key={relatedProduct.id} product={relatedProduct} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KidsProductDetailPage;